import type { PersistentUserSession } from '@mimir/client-common/util/persistentUserSession';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import type { Route } from 'vue-router/types/router';
import type { StoreType } from './state';
import { isClientConfig } from '@mimir/common/dto/ClientConfigDto';
import type { AppRoutingState } from '@mimir/web/sharedState/appRoutingState';
import { initializeGlobalBackendClient } from '@mimir/client-common/globals/initializeGlobalBackendClient';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import { updateGoogleMapPromise } from '@mimir/web/globals/variables';
import type { QuickPreview } from '@mimir/web/model/quickPreviewOps';
import type { TimedMdf } from '@mimir/web/model/timedMdf';
import type { PlayerServices } from '@mimir/web/model/playerServices';
import { listenToPostMessage, notifyMimirIsLoaded, trySetUpPostMessageRelatedState } from '@mimir/web/util/postMessageHelpers';
import { createItemMediaPlayer } from '@mimir/web/model/createItemMediaPlayer';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { ItemDetailsState } from '../model/itemDetailsState';

export async function initializeStore(
  serviceCache: ServiceCache,
  session: PersistentUserSession,
  store: StoreType,
  appRoutingState: AppRoutingState,
  destinationRoute: Route,
  applicationContext: ApplicationContext,
  quickPreview: QuickPreview,
  timedMdf: TimedMdf,
  itemDetailsState: ItemDetailsState,
  playerServices: PlayerServices,
) {
  /*
    We need to initialize and bootstrap the store and other global state, so
    that among other things the client configuration is available, such that
    things like the static API layer that poke global state through config
    getGlobalServiceDocument() will work.
  */
  await initialize(serviceCache, store);
  const bootstrap = await import('../bootstrap');
  await bootstrap.bootstrap(
    applicationContext,
    session,
    store,
    quickPreview,
    timedMdf,
    destinationRoute
  );

  appRoutingState.storeInitialized = true;

  const tenantConfig: TenantConfigDto = store.getters.tenantConfig;

  const itemDetailsPageMediaPlayerOps = createItemMediaPlayer(
    playerServices,
    tenantConfig.featureFlags.useCutterPlayer ?? false,
    { useEditProxy: false, id: 'itemDetailsPage', pollBufferedIntervalMs: 500 }
  );

  itemDetailsState.setPlayer(itemDetailsPageMediaPlayerOps);

  const quickPreviewMediaPlayerOps = createItemMediaPlayer(
    playerServices,
    tenantConfig.featureFlags.useCutterPlayer,
    { id: 'quickPreview', pollBufferedIntervalMs: 500 }
  );

  quickPreview.ops.setPlayer(quickPreviewMediaPlayerOps);

  if (trySetUpPostMessageRelatedState(applicationContext)) {
    listenToPostMessage(applicationContext, quickPreview);
    notifyMimirIsLoaded();
  }
}

async function loadGoogleMap(apiKey: string | null, store: StoreType) {

  try {
    if (!apiKey) return;
    //If google is blocked, then we can't load the library
    const { Loader } = await import('@googlemaps/js-api-loader');
    const loader = new Loader({
      apiKey,
      libraries: ['places']
    });
    await loader.load();
  } catch (err) {
    console.error('Error loading Google Maps API', err);
  }
}

const initialize = async (serviceCache: ServiceCache, store: StoreType) => {
  const serviceDocument = await serviceCache.getServiceDocument();

  if (!serviceDocument) {
    throw new Error('No service document found.');
  }
  const clientConfig = serviceDocument.clientConfig;
  if (!isClientConfig(clientConfig)) {
    throw new Error('No clientConfig found.');
  }

  initializeGlobalBackendClient(serviceDocument);

  updateGoogleMapPromise(loadGoogleMap(clientConfig.googleMapsApiKey, store));
};
